import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Google Fonts inject — cinematic editorial serif + refined sans
const injectFonts = () => {
  const ID = "mf-desire-fonts";
  if (document.getElementById(ID)) return;
  const l1 = document.createElement("link");
  l1.id = ID;
  l1.rel = "stylesheet";
  l1.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;0,900;1,400&family=Inter:wght@300;400;500;600;700;800&display=swap";
  document.head.appendChild(l1);
};

const SERIF = "'Playfair Display', 'Cormorant Garamond', Georgia, serif";
const SANS = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// ─── Ambient jewel-tone background ─────────────────────────────────
const Ambient = ({ accentHex, brassHex }) => (
  <div aria-hidden="true" style={{
    position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
    background: `
      radial-gradient(1200px 700px at 15% 12%, ${accentHex}44 0%, transparent 62%),
      radial-gradient(900px 620px at 88% 78%, ${brassHex}33 0%, transparent 60%),
      radial-gradient(600px 400px at 50% 100%, ${accentHex}22 0%, transparent 70%),
      linear-gradient(180deg, #06111e 0%, #0a1929 40%, #0c1220 100%)
    `,
    transition: "background 1.2s cubic-bezier(0.22, 1, 0.36, 1)",
  }} />
);

// ─── Grain overlay for editorial texture ───────────────────────────
const Grain = () => (
  <div aria-hidden="true" style={{
    position: "fixed", inset: 0, zIndex: 1, pointerEvents: "none",
    opacity: 0.05, mixBlendMode: "overlay",
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
  }} />
);

// ─── Small caps eyebrow ────────────────────────────────────────────
const Eyebrow = ({ children, color = "#B8915A" }) => (
  <div style={{
    color, fontFamily: SANS, fontSize: "0.7rem",
    letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 800,
    marginBottom: "0.75rem"
  }}>{children}</div>
);

// ─── Doctrine Card (locked financial pledge) ───────────────────────
const DoctrineCard = ({ fee, brassHex, accentHex }) => {
  if (!fee) return null;
  const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  return (
    <div data-testid="doctrine-card" style={{
      position: "relative", background: "rgba(6, 17, 30, 0.7)",
      border: `1px solid ${brassHex}55`, borderRadius: 14,
      padding: "2rem 2.2rem", backdropFilter: "blur(20px)",
      boxShadow: `0 40px 90px rgba(0,0,0,0.5), 0 0 60px ${accentHex}22`,
    }}>
      <Eyebrow color={brassHex}>The Doctrine · Locked</Eyebrow>
      <h3 style={{
        fontFamily: SERIF, fontSize: "1.9rem", fontWeight: 700,
        color: "#F5EFE1", margin: "0 0 1.5rem", lineHeight: 1.15,
        fontStyle: "italic",
      }}>
        Materials first. <span style={{ color: brassHex }}>Service last.</span>
      </h3>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.2rem", marginBottom: "1.5rem" }}>
        {/* Phase 1 */}
        <div data-testid="doctrine-phase-1" style={{
          background: "rgba(0,220,120,0.06)", border: "1px solid rgba(0,220,120,0.35)",
          borderRadius: 10, padding: "1.1rem 1.2rem", position: "relative",
        }}>
          <Eyebrow color="#00DC78">Phase 1 · Mobilization</Eyebrow>
          <div style={{ fontFamily: SERIF, fontSize: "2.4rem", fontWeight: 700, color: "#00DC78", lineHeight: 1, letterSpacing: "-0.02em" }} data-testid="phase-1-cost">
            $0
          </div>
          <div style={{ fontFamily: SANS, fontSize: "0.78rem", color: "#ADBED1", marginTop: 6, lineHeight: 1.55 }}>
            User procurement cost at kickoff.<br />
            Materials are covered by Activation Capital already deployed.
          </div>
        </div>

        {/* Phase 2 */}
        <div data-testid="doctrine-phase-2" style={{
          background: `${brassHex}15`, border: `1px solid ${brassHex}66`,
          borderRadius: 10, padding: "1.1rem 1.2rem",
        }}>
          <Eyebrow color={brassHex}>Phase 2 · Stabilization</Eyebrow>
          <div style={{ fontFamily: SERIF, fontSize: "2.4rem", fontWeight: 700, color: brassHex, lineHeight: 1, letterSpacing: "-0.02em" }} data-testid="phase-2-fee">
            {fmt(fee.service_fee_monthly)}<span style={{ fontSize: "0.9rem", opacity: 0.75, marginLeft: 6 }}>/mo</span>
          </div>
          <div style={{ fontFamily: SANS, fontSize: "0.78rem", color: "#ADBED1", marginTop: 6, lineHeight: 1.55 }}>
            Service Fee at (Market Rent × 3) + 20%.<br />
            Released monthly on PM sign-off — after stabilization.
          </div>
        </div>
      </div>

      {/* ROI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1rem", paddingTop: "1.2rem", borderTop: `1px solid ${brassHex}33` }}>
        <div>
          <div style={{ color: "#9CA3AF", fontSize: "0.68rem", letterSpacing: "0.16em", textTransform: "uppercase", fontFamily: SANS, fontWeight: 700 }}>Market Rent</div>
          <div style={{ color: "#F5EFE1", fontSize: "1.15rem", fontFamily: SERIF, fontWeight: 600, marginTop: 4 }} data-testid="roi-market-rent">{fmt(fee.market_rent)}</div>
        </div>
        <div>
          <div style={{ color: "#9CA3AF", fontSize: "0.68rem", letterSpacing: "0.16em", textTransform: "uppercase", fontFamily: SANS, fontWeight: 700 }}>Stabilized Target</div>
          <div style={{ color: "#00DC78", fontSize: "1.15rem", fontFamily: SERIF, fontWeight: 700, marginTop: 4 }} data-testid="roi-stabilized">{fmt(fee.stabilized_rent_target)}</div>
        </div>
        <div>
          <div style={{ color: "#9CA3AF", fontSize: "0.68rem", letterSpacing: "0.16em", textTransform: "uppercase", fontFamily: SANS, fontWeight: 700 }}>Annual Fee</div>
          <div style={{ color: brassHex, fontSize: "1.15rem", fontFamily: SERIF, fontWeight: 700, marginTop: 4 }} data-testid="roi-annual">{fmt(fee.service_fee_annual)}</div>
        </div>
      </div>
    </div>
  );
};

// ─── Theme Card in gallery ────────────────────────────────────────
const ThemeCard = ({ theme, active, onClick, delay }) => (
  <button type="button" onClick={onClick} data-testid={`theme-card-${theme.theme_key}`}
    style={{
      position: "relative", background: `linear-gradient(160deg, ${theme.accent_hex}22, ${theme.brass_hex}12)`,
      border: `1px solid ${active ? theme.brass_hex : "rgba(255,255,255,0.08)"}`,
      borderRadius: 12, padding: "1.4rem 1.4rem 1.5rem",
      cursor: "pointer", textAlign: "left", overflow: "hidden",
      transform: active ? "translateY(-4px)" : "translateY(0)",
      boxShadow: active ? `0 20px 40px ${theme.accent_hex}55, 0 0 30px ${theme.brass_hex}44` : "0 8px 20px rgba(0,0,0,0.25)",
      transition: "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.5s ease, border-color 0.4s ease",
      animation: `reveal 0.9s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms both`,
      color: "#F5EFE1",
    }}>
    {/* corner accent */}
    <div style={{
      position: "absolute", top: 0, right: 0, width: 90, height: 90,
      background: `linear-gradient(225deg, ${theme.brass_hex}66 0%, transparent 65%)`,
      pointerEvents: "none",
    }} />
    <Eyebrow color={theme.brass_hex}>{theme.theme_key.replace(/_/g, " · ")}</Eyebrow>
    <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
      <span style={{ width: 32, height: 32, borderRadius: 6, background: theme.accent_hex, boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.12)" }} />
      <span style={{ width: 32, height: 32, borderRadius: 6, background: theme.brass_hex, boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.12)" }} />
      <span style={{ width: 32, height: 32, borderRadius: 6, background: "#f5efe1", boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.12)" }} />
    </div>
    <div style={{ fontFamily: SERIF, fontSize: "1.1rem", fontWeight: 600, lineHeight: 1.35, color: "#F5EFE1", marginBottom: 6 }}>
      {theme.narrative.split(".")[0]}.
    </div>
    <div style={{ fontFamily: SANS, fontSize: "0.78rem", color: "#ADBED1", lineHeight: 1.55 }}>
      {theme.narrative.split(".").slice(1).join(".").trim()}
    </div>
  </button>
);

// ─── Materials — staggered reveal ─────────────────────────────────
const MaterialLines = ({ lines, brassHex }) => (
  <div data-testid="materials-reveal" style={{ display: "grid", gap: 10 }}>
    {lines.map((l, i) => (
      <div key={`${l.role}-${i}`} data-testid={`material-line-${l.role}`}
        style={{
          display: "flex", justifyContent: "space-between", alignItems: "baseline",
          padding: "0.85rem 1rem", borderBottom: "1px solid rgba(255,255,255,0.08)",
          animation: `slideIn 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${i * 60}ms both`,
        }}>
        <div>
          <div style={{ color: brassHex, fontFamily: SANS, fontSize: "0.66rem", letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 700 }}>{l.role_label}</div>
          <div style={{ color: "#F5EFE1", fontFamily: SERIF, fontSize: "1.05rem", fontWeight: 500, marginTop: 3 }}>{l.system_label}</div>
        </div>
      </div>
    ))}
  </div>
);

// ─── Main component ─────────────────────────────────────────────────
const DesireStudioPreview = () => {
  const [themes, setThemes] = useState([]);
  const [unitConfigs, setUnitConfigs] = useState([]);
  const [stats, setStats] = useState(null);

  const [seed, setSeed] = useState(() => {
    if (typeof window !== "undefined") {
      const s = new URLSearchParams(window.location.search).get("seed");
      if (s) return s;
    }
    return `preview-${Math.random().toString(36).slice(2, 10)}`;
  });
  const [unitConfig, setUnitConfig] = useState(() => {
    if (typeof window !== "undefined") {
      const u = new URLSearchParams(window.location.search).get("unit_config");
      if (u) return u;
    }
    return "2br_1ba";
  });
  const [marketRent, setMarketRent] = useState(() => {
    if (typeof window !== "undefined") {
      const r = new URLSearchParams(window.location.search).get("market_rent");
      if (r && !isNaN(Number(r))) return Number(r);
    }
    return 1450;
  });
  // Property context — when provided, each render is logged to the
  // variant registry so the LP can pull a report of unique variations.
  const [propertyId] = useState(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("property_id") || "";
  });
  const [unitId] = useState(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("unit_id") || "";
  });
  const [report, setReport] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    injectFonts();
    fetch(`${BACKEND_URL}/api/public/desire-studio/themes`)
      .then((r) => r.ok ? r.json() : { themes: [] })
      .then((d) => setThemes(d.themes || []))
      .catch(() => setThemes([]));
    fetch(`${BACKEND_URL}/api/public/desire-studio/unit-configs`)
      .then((r) => r.ok ? r.json() : { unit_configs: [] })
      .then((d) => setUnitConfigs(d.unit_configs || []))
      .catch(() => setUnitConfigs([]));
    fetch(`${BACKEND_URL}/api/public/desire-studio/stats`)
      .then((r) => r.ok ? r.json() : null)
      .then(setStats)
      .catch(() => setStats(null));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({
      seed, unit_config: unitConfig, market_rent: String(marketRent), rotation_offset: String(rotation),
    });
    if (propertyId) params.set("property_id", propertyId);
    if (unitId || propertyId) params.set("unit_id", unitId || seed);
    fetch(`${BACKEND_URL}/api/public/desire-studio/preview?${params.toString()}`)
      .then((r) => r.ok ? r.json() : null)
      .then(setPreview)
      .catch(() => setPreview(null))
      .finally(() => setLoading(false));
    // Refresh property report after each render (variant may have just been logged)
    if (propertyId) {
      // Small debounce so upsert completes before the report reads
      setTimeout(() => {
        fetch(`${BACKEND_URL}/api/public/desire-studio/property-report/${encodeURIComponent(propertyId)}`)
          .then((r) => r.ok ? r.json() : null)
          .then(setReport)
          .catch(() => setReport(null));
      }, 350);
    }
  }, [seed, unitConfig, marketRent, rotation, propertyId, unitId]);

  const brassHex = preview?.variant?.brass_hex || "#B8915A";
  const accentHex = preview?.variant?.accent_hex || "#0d5c3a";

  const rollNew = () => {
    setSeed(`preview-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36).slice(-4)}`);
    setRotation(0);
  };
  const nudge = () => setRotation((r) => r + 1);

  const shareLink = useMemo(() => {
    if (typeof window === "undefined") return "";
    const p = new URLSearchParams({ seed, unit_config: unitConfig, market_rent: String(marketRent) });
    return `${window.location.origin}/desire-studio-preview?${p.toString()}`;
  }, [seed, unitConfig, marketRent]);

  const activeTheme = preview?.variant;

  return (
    <div data-testid="desire-studio-preview" style={{
      position: "relative", minHeight: "100vh",
      fontFamily: SANS, color: "#F5EFE1", overflow: "hidden",
    }}>
      {/* keyframes */}
      <style>{`
        @keyframes reveal {
          from { opacity: 0; transform: translateY(24px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-16px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }
        .ds-hero-title { font-family: ${SERIF}; letter-spacing: -0.02em; line-height: 1.02; }
        .ds-cta:hover  { transform: translateY(-2px); box-shadow: 0 20px 40px rgba(0,0,0,0.4); }
        .ds-cta        { transition: transform 0.3s ease, box-shadow 0.3s ease; }
      `}</style>

      <Ambient accentHex={accentHex} brassHex={brassHex} />
      <Grain />

      <div style={{ position: "relative", zIndex: 2 }}>
        {/* Top strip */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "1.5rem 2.5rem", borderBottom: "1px solid rgba(255,255,255,0.06)",
          backdropFilter: "blur(8px)",
        }}>
          <Link to="/" style={{
            fontFamily: SERIF, fontStyle: "italic", fontSize: "1.15rem",
            color: "#F5EFE1", textDecoration: "none", letterSpacing: "-0.01em"
          }} data-testid="ds-back-home">← Moovin Forward</Link>
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            {stats && (
              <div data-testid="ds-social-proof" style={{
                fontFamily: SANS, fontSize: "0.72rem", color: "#ADBED1",
                letterSpacing: "0.14em", textTransform: "uppercase"
              }}>
                <span style={{ color: brassHex, fontWeight: 800 }}>{stats.rendered_this_week}</span> uniquely rendered variations · this week
              </div>
            )}
            <Link to="/#contact" className="ds-cta" data-testid="ds-book-walkthrough" style={{
              background: `linear-gradient(90deg, ${brassHex}, ${accentHex})`,
              color: "#0a1929", padding: "0.7rem 1.4rem", borderRadius: 6,
              fontFamily: SANS, fontSize: "0.78rem", fontWeight: 800,
              letterSpacing: "0.08em", textTransform: "uppercase",
              textDecoration: "none",
            }}>Request Walkthrough</Link>
          </div>
        </div>

        {/* HERO */}
        <section style={{ padding: "4.5rem 2.5rem 3rem", maxWidth: 1240, margin: "0 auto" }}>
          <Eyebrow color={brassHex}>The Desire Studio · Proprietary Framework</Eyebrow>
          <h1 className="ds-hero-title" data-testid="ds-hero-title" style={{
            fontSize: "clamp(2.8rem, 6vw, 5.2rem)", fontWeight: 700, margin: "0 0 1.4rem",
            maxWidth: 980, color: "#F5EFE1",
          }}>
            Portfolio-scale interior identity, <br />
            <span style={{ color: brassHex, fontStyle: "italic" }}>generated per address.</span>
          </h1>

          {/* Look-and-Lease Effect badge — visible when property context is supplied */}
          {propertyId && report && (
            <div data-testid="look-and-lease-badge" style={{
              display: "inline-flex", alignItems: "center", gap: 14,
              background: `linear-gradient(90deg, ${brassHex}22, ${accentHex}22)`,
              border: `1px solid ${brassHex}88`, borderRadius: 999,
              padding: "0.65rem 1.3rem", marginBottom: "1.4rem",
              backdropFilter: "blur(10px)",
              animation: "reveal 0.9s cubic-bezier(0.22, 1, 0.36, 1)",
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: "50%",
                background: `radial-gradient(circle, ${brassHex} 0%, ${accentHex} 100%)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: SERIF, fontSize: "1.1rem", fontWeight: 700, color: "#0a1929",
              }} data-testid="unique-variations-count">{report.unique_variations || 0}</div>
              <div>
                <div style={{ fontFamily: SANS, fontSize: "0.66rem", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 800, color: brassHex }}>
                  Look-and-Lease · Portfolio Tracking
                </div>
                <div style={{ fontFamily: SERIF, fontSize: "0.98rem", color: "#F5EFE1", fontStyle: "italic", marginTop: 2 }} data-testid="report-property-caption">
                  distinct variations registered for <strong style={{ fontStyle: "normal", color: brassHex }}>{propertyId.replace(/-/g, " ")}</strong>
                </div>
              </div>
            </div>
          )}

          <p style={{
            fontFamily: SANS, fontSize: "1.15rem", color: "#ADBED1",
            maxWidth: 720, lineHeight: 1.65, margin: "0 0 2.4rem",
          }}>
            Assignment is deterministic on address hash. Adjacent units within the same regional asset folder are programmatically barred from receiving identical variations. Cost-efficient modular procurement. Institutional-grade material specification. Full portfolio capability disclosure upon engagement.
          </p>

          {/* Config strip */}
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "0.7rem", padding: "1.2rem 1.4rem",
            background: "rgba(6,17,30,0.55)", border: `1px solid ${brassHex}33`,
            borderRadius: 10, backdropFilter: "blur(12px)", marginBottom: "1.8rem",
          }}>
            <div>
              <Eyebrow color={brassHex}>Unit</Eyebrow>
              <select data-testid="ds-unit-config" value={unitConfig} onChange={(e) => setUnitConfig(e.target.value)}
                style={{ width: "100%", background: "transparent", color: "#F5EFE1",
                  border: `1px solid ${brassHex}44`, borderRadius: 5, padding: "0.55rem 0.7rem",
                  fontFamily: SANS, fontSize: "0.92rem", cursor: "pointer" }}>
                {unitConfigs.map((u) => <option key={u.key} value={u.key} style={{ background: "#0a1929" }}>{u.label}</option>)}
              </select>
            </div>
            <div>
              <Eyebrow color={brassHex}>Market Rent</Eyebrow>
              <input type="number" data-testid="ds-market-rent" value={marketRent}
                onChange={(e) => setMarketRent(Math.max(0, Number(e.target.value) || 0))}
                min="0" max="10000" step="25"
                style={{ width: "100%", background: "transparent", color: "#F5EFE1",
                  border: `1px solid ${brassHex}44`, borderRadius: 5, padding: "0.55rem 0.7rem",
                  fontFamily: SANS, fontSize: "0.92rem" }} />
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 6 }}>
              <button type="button" onClick={rollNew} data-testid="ds-roll-btn" className="ds-cta"
                style={{ flex: 1, background: `linear-gradient(90deg, ${brassHex}, ${accentHex})`,
                  color: "#0a1929", border: "none", padding: "0.6rem 0.8rem",
                  borderRadius: 5, fontFamily: SANS, fontSize: "0.78rem", fontWeight: 800,
                  letterSpacing: "0.06em", textTransform: "uppercase", cursor: "pointer" }}>
                🎲 New Variation
              </button>
              <button type="button" onClick={nudge} data-testid="ds-nudge-btn"
                style={{ background: "transparent", color: brassHex, border: `1px solid ${brassHex}88`,
                  padding: "0.55rem 0.75rem", borderRadius: 5, fontFamily: SANS,
                  fontSize: "0.78rem", fontWeight: 700, cursor: "pointer" }}>
                Nudge →
              </button>
            </div>
          </div>

          {/* Active variant reveal */}
          {activeTheme && (
            <div data-testid="active-variant-reveal" style={{
              display: "grid", gridTemplateColumns: "1.15fr 1fr", gap: "2.4rem",
              alignItems: "start",
            }}>
              <div style={{ animation: "reveal 0.8s cubic-bezier(0.22, 1, 0.36, 1)" }}>
                <Eyebrow color={brassHex}>Currently Generated · {activeTheme.theme_key.replace(/_/g, " ")}</Eyebrow>
                <h2 style={{
                  fontFamily: SERIF, fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)",
                  fontStyle: "italic", fontWeight: 600, color: "#F5EFE1",
                  margin: "0 0 1.2rem", lineHeight: 1.15,
                }} data-testid="active-variant-headline">
                  {activeTheme.narrative.split(".")[0]}.
                </h2>
                <p style={{ fontFamily: SANS, fontSize: "1rem", color: "#ADBED1", lineHeight: 1.7, margin: "0 0 1.5rem", maxWidth: 560 }}>
                  {activeTheme.narrative.split(".").slice(1).join(".").trim()}
                </p>

                {/* Dimension chips — the 4-axis permutation exposed */}
                {activeTheme.dimensions && (
                  <div data-testid="variant-dimensions" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: "1.4rem" }}>
                    {[
                      { k: "jewel_tone_label", label: "Jewel Tone" },
                      { k: "hardware_label",   label: "Hardware" },
                      { k: "wood_label",       label: "Millwork" },
                      { k: "texture_label",    label: "Texture" },
                    ].map((chip, i) => (
                      <div key={chip.k} data-testid={`dim-chip-${chip.k}`} style={{
                        background: "rgba(6,17,30,0.55)",
                        border: `1px solid ${brassHex}55`,
                        borderRadius: 999, padding: "0.4rem 0.9rem",
                        fontFamily: SANS, fontSize: "0.72rem",
                        animation: `reveal 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${i * 90}ms both`,
                      }}>
                        <span style={{ color: brassHex, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", marginRight: 6, fontSize: "0.6rem" }}>{chip.label}</span>
                        <span style={{ color: "#F5EFE1", fontWeight: 600 }}>{activeTheme.dimensions[chip.k]}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* palette swatches */}
                <div style={{ display: "flex", gap: 12, marginBottom: "1.5rem" }} data-testid="active-swatches">
                  {[activeTheme.accent_hex, activeTheme.brass_hex, "#f5efe1", "#1a2537"].map((c, i) => (
                    <div key={i} style={{
                      width: 56, height: 72, borderRadius: 8, background: c,
                      boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.15), 0 10px 24px ${c}66`,
                      animation: `reveal 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${i * 120}ms both`,
                    }} />
                  ))}
                </div>

                <div style={{ fontFamily: SANS, fontSize: "0.72rem", color: "#9CA3AF",
                  letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 700 }}>
                  Preview seed · <span data-testid="ds-active-seed" style={{ color: brassHex }}>{seed}</span>
                  <span style={{ marginLeft: 12 }}>· <span style={{ color: brassHex }}>108</span> total permutations</span>
                </div>
              </div>

              {/* Doctrine */}
              {preview?.fee && (
                <div style={{ animation: "reveal 0.9s cubic-bezier(0.22, 1, 0.36, 1) 150ms both" }}>
                  <DoctrineCard fee={preview.fee} brassHex={brassHex} accentHex={accentHex} />
                </div>
              )}
            </div>
          )}
        </section>

        {/* MATERIALS SECTION */}
        {preview?.lines?.length > 0 && (
          <section style={{ padding: "3rem 2.5rem", maxWidth: 1240, margin: "0 auto" }}>
            <Eyebrow color={brassHex}>Materials Auto-Selected · Modular · Repeatable</Eyebrow>
            <h2 style={{ fontFamily: SERIF, fontSize: "2rem", fontWeight: 700, color: "#F5EFE1", margin: "0 0 2rem" }}>
              Installed material specification.
            </h2>
            <div style={{
              background: "rgba(6,17,30,0.55)", border: `1px solid ${brassHex}33`,
              borderRadius: 12, padding: "0.5rem 1.5rem 1rem", backdropFilter: "blur(12px)",
            }}>
              <MaterialLines lines={preview.lines} brassHex={brassHex} />
            </div>
            <div style={{ fontFamily: SANS, fontSize: "0.82rem", color: "#9CA3AF", marginTop: "1.4rem", maxWidth: 720, lineHeight: 1.65, fontStyle: "italic" }}>
              What is rendered is what is installed. Every specified material is stocked in our repeatable procurement catalog: institutional-grade material specification at cost-efficient procurement pricing.
            </div>
          </section>
        )}

        {/* GALLERY */}
        <section style={{ padding: "3rem 2.5rem", maxWidth: 1240, margin: "0 auto" }}>
          <Eyebrow color={brassHex}>The Framework · Proprietary Variation Lexicon</Eyebrow>
          <h2 style={{ fontFamily: SERIF, fontSize: "2.4rem", fontWeight: 700, color: "#F5EFE1", margin: "0 0 0.5rem" }}>
              The full jewel-tone <span style={{ fontStyle: "italic", color: brassHex }}>lexicon.</span>
          </h2>
          <p style={{ fontFamily: SANS, fontSize: "1rem", color: "#ADBED1", maxWidth: 700, marginBottom: "2rem", lineHeight: 1.7 }}>
            Select any variation to preview against the current unit configuration. Assignment is rotated per address; a 50-unit portfolio traverses the full permutation lexicon prior to any repeat.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.2rem" }}>
            {themes.map((t, i) => (
              <ThemeCard key={t.theme_key} theme={t}
                active={t.theme_key === activeTheme?.theme_key}
                onClick={() => {
                  // Seed the preview so it lands on this theme by scanning offsets.
                  for (let offset = 0; offset < 6; offset++) {
                    const nextIdx = ((activeTheme?.theme_index ?? 0) + offset) % 6;
                    if (themes[nextIdx]?.theme_key === t.theme_key) {
                      setRotation((r) => r + offset);
                      break;
                    }
                  }
                }}
                delay={i * 80}
              />
            ))}
          </div>
        </section>

        {/* COMPARISON STRIP */}
        <section style={{ padding: "4rem 2.5rem", maxWidth: 1240, margin: "0 auto" }}>
          <Eyebrow color={brassHex}>Industry Baseline · vs · Moovin Forward Framework</Eyebrow>
          <h2 style={{ fontFamily: SERIF, fontSize: "2.4rem", fontWeight: 700, color: "#F5EFE1", margin: "0 0 2rem" }}>
            Commoditized turnover carries <span style={{ fontStyle: "italic" }}>portfolio-level risk.</span>
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }} data-testid="ds-comparison">
            <div style={{
              background: "linear-gradient(160deg, #d1cec3, #b5b1a3)",
              border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12,
              padding: "2rem", color: "#3a3628", position: "relative", overflow: "hidden",
            }}>
              <div style={{ fontFamily: SANS, fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.28em", textTransform: "uppercase", opacity: 0.7 }}>
                Industry Baseline Delivery
              </div>
              <h3 style={{ fontFamily: SERIF, fontSize: "1.6rem", fontStyle: "italic", margin: "0.6rem 0 1rem" }}>
                Identical specification across the portfolio.
              </h3>
              <ul style={{ margin: 0, padding: "0 0 0 1.1rem", lineHeight: 1.7, fontFamily: SANS, fontSize: "0.92rem" }}>
                <li>Uniform paint, flooring, and fixture SKUs across every unit</li>
                <li>Tenant personalization triggers move-out chargebacks</li>
                <li>Listing photography materially misrepresents delivered condition</li>
                <li>Commoditized execution compresses achievable rent premium</li>
              </ul>
            </div>

            <div style={{
              background: `linear-gradient(160deg, ${accentHex}, #0a1929)`,
              border: `1px solid ${brassHex}66`, borderRadius: 12,
              padding: "2rem", color: "#F5EFE1", position: "relative", overflow: "hidden",
              boxShadow: `0 30px 60px ${accentHex}55`,
            }}>
              <div aria-hidden="true" style={{ position: "absolute", top: 0, right: 0, width: 140, height: 140,
                background: `radial-gradient(circle at top right, ${brassHex}88, transparent 60%)` }} />
              <div style={{ fontFamily: SANS, fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.28em", textTransform: "uppercase", color: brassHex }}>
                The Moovin Forward Framework
              </div>
              <h3 style={{ fontFamily: SERIF, fontSize: "1.6rem", fontStyle: "italic", margin: "0.6rem 0 1rem", color: "#F5EFE1" }}>
                Deterministic per-address differentiation.
              </h3>
              <ul style={{ margin: 0, padding: "0 0 0 1.1rem", lineHeight: 1.7, fontFamily: SANS, fontSize: "0.92rem", color: "#F5EFE1" }}>
                <li>Variation code auto-assigned per address; adjacency collisions prevented</li>
                <li>Rendered concept locks the installed specification — no delivery drift</li>
                <li>50 / 50 disbursement: Phase 1 user procurement cost = <strong style={{ color: brassHex }}>$0</strong></li>
                <li>Modular procurement · institutional material spec · TREC scope-disciplined</li>
              </ul>
            </div>
          </div>
        </section>

        {/* PROPRIETARY NOTICE + CTA */}
        <section style={{ padding: "4rem 2.5rem 6rem", maxWidth: 1000, margin: "0 auto", textAlign: "center" }}>
          <Eyebrow color={brassHex}>Proprietary Framework · Institutional Delivery</Eyebrow>
          <h2 style={{
            fontFamily: SERIF, fontSize: "clamp(2rem, 4.5vw, 3.5rem)", fontStyle: "italic",
            fontWeight: 700, color: "#F5EFE1", margin: "0 0 1.4rem", lineHeight: 1.1,
          }}>
            The Desire Studio is <span style={{ color: brassHex }}>proprietary.</span>
          </h2>
          <p style={{ fontFamily: SANS, fontSize: "1.05rem", color: "#ADBED1", lineHeight: 1.7, marginBottom: "2.4rem", maxWidth: 780, margin: "0 auto 2.4rem" }}>
            {preview?.proprietary_notice || "Every variation is generated from a specification framework not available at other firms. Engage a walkthrough and Moovin Forward will lock the variation assigned to the identified asset — enabling verification of the exact installed specification prior to mobilization."}
          </p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/#contact" className="ds-cta" data-testid="ds-cta-primary" style={{
              background: `linear-gradient(90deg, ${brassHex}, ${accentHex})`,
              color: "#0a1929", padding: "1rem 2.2rem", borderRadius: 6,
              fontFamily: SANS, fontSize: "0.88rem", fontWeight: 800,
              letterSpacing: "0.14em", textTransform: "uppercase",
              textDecoration: "none",
            }}>Request Institutional Walkthrough</Link>
            <button type="button" data-testid="ds-share-link" onClick={() => {
              try { navigator.clipboard.writeText(shareLink); } catch (e) { /* graceful */ void e; }
            }} style={{
              background: "transparent", color: brassHex, border: `1px solid ${brassHex}88`,
              padding: "1rem 2rem", borderRadius: 6, cursor: "pointer",
              fontFamily: SANS, fontSize: "0.82rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase",
            }}>Copy Shareable Preview Link</button>
          </div>
        </section>

        {/* Footer */}
        <footer style={{ padding: "2rem 2.5rem", textAlign: "center", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ fontFamily: SANS, fontSize: "0.72rem", color: "#6B7280", letterSpacing: "0.14em", textTransform: "uppercase" }}>
            Moovin Forward LLC · Institutional Interior Stabilization · DFW Metroplex · TREC Scope-Disciplined · Est. 2025
          </div>
        </footer>

        {loading && !preview && (
          <div data-testid="ds-loading" style={{ position: "fixed", top: 20, right: 20, color: brassHex, fontFamily: SANS, fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase" }}>
            Generating…
          </div>
        )}
      </div>
    </div>
  );
};

export default DesireStudioPreview;
